"use client";

/**
 * <RightRail /> — 우측 sticky 패널.
 *
 * Tabs (사용자 결정 2026-05-21): 관심 종목 / AI 애널리스트 / 메모장 (순서)
 *   - 관심 종목 = WatchlistPanel (localStorage 'dashboard:watchlist')
 *   - AI = ChartChatSidebar (활성 탭 ticker context)
 *   - 메모 = BlockNote 에디터
 */

import dynamic from "next/dynamic";
import { Star, Sparkles, NotebookPen } from "lucide-react";
import { ChartChatSidebar } from "@/components/chart-lab/chart-chat-sidebar";
import { WatchlistPanel } from "@/components/layout/watchlist-panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useTabs } from "@/contexts/tabs-context";

// BlockNote 는 SSR 미지원 → dynamic import
const MemoEditor = dynamic(
  () => import("@/components/memo/memo-editor").then((m) => m.MemoEditor),
  { ssr: false, loading: () => <div className="p-[var(--bs-space-3)] text-[length:var(--bs-fs-sm)] text-[color:var(--bs-gray-500)]">로드 중…</div> }
);

export function RightRail() {
  const { tabs, activeTabId } = useTabs();
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const symbol = activeTab?.type === "analyze" && activeTab.ticker ? activeTab.ticker : "";

  return (
    <aside
      aria-label="우측 패널"
      // 사용자 결정 2026-05-17: pt-[44px] 폐기 — 그 자리 (TabBar 높이 영역) 에 BITE AI
      // 로고 헤더를 직접 mount. 빈 padding 위에 헤더 두는 게 아니라 padding 자체가 헤더.
      style={{ width: "var(--bs-rr-width, 320px)" }}
      className="fixed right-0 top-0 z-20 flex h-screen flex-col bg-[var(--bs-paper)] max-[1100px]:hidden"
    >
      {/* Bite AI 헤더 — h-[44px]. 가운데 정렬, 구분선 X (사용자 결정 2026-05-17). */}
      <div className="h-[44px] flex items-center justify-center gap-2 px-[var(--bs-space-3)] flex-shrink-0">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/bite-logo.png" alt="Bite" className="w-7 h-7 flex-shrink-0" />
        <span className="text-[length:var(--bs-fs-md)] font-semibold text-[color:var(--bs-ink)] tracking-tight leading-none">
          Bite{" "}
          <span
            className="font-semibold tracking-tight"
            style={{
              backgroundImage: "linear-gradient(135deg, var(--bs-red-500) 0%, #FF6B9D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            AI
          </span>
        </span>
      </div>
      <Tabs defaultValue="watchlist" className="flex h-full min-h-0 flex-col">
        <TabsList className="grid grid-cols-3 rounded-none border-b border-[var(--bs-gray-200)] bg-transparent p-0 h-auto">
          <TabsTrigger value="watchlist" className="flex items-center justify-center gap-[var(--bs-space-1)] py-[var(--bs-space-3)] text-[length:var(--bs-fs-2xs)] rounded-none border-b border-transparent data-[state=active]:border-[var(--bs-red-500)] data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            <Star className="h-3.5 w-3.5" />
            <span>관심 종목</span>
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center justify-center gap-[var(--bs-space-1)] py-[var(--bs-space-3)] text-[length:var(--bs-fs-2xs)] rounded-none border-b border-transparent data-[state=active]:border-[var(--bs-red-500)] data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            <Sparkles className="h-3.5 w-3.5" />
            <span>AI 애널리스트</span>
          </TabsTrigger>
          <TabsTrigger value="memo" className="flex items-center justify-center gap-[var(--bs-space-1)] py-[var(--bs-space-3)] text-[length:var(--bs-fs-2xs)] rounded-none border-b border-transparent data-[state=active]:border-[var(--bs-red-500)] data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            <NotebookPen className="h-3.5 w-3.5" />
            <span>메모장</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="watchlist" className="m-0 min-h-0 flex-1 overflow-hidden">
          <WatchlistPanel />
        </TabsContent>
        <TabsContent value="ai" className="m-0 min-h-0 flex-1 overflow-hidden">
          <ChartChatSidebar symbol={symbol} />
        </TabsContent>
        <TabsContent value="memo" className="m-0 min-h-0 flex-1 overflow-y-auto">
          <MemoEditor scope="anonymous" ticker={symbol || undefined} />
        </TabsContent>
      </Tabs>
    </aside>
  );
}
